import { ProductFeatureId } from '@/actions/permissionActions'
import { ReduxState } from '@/utils/typeHelpers'
import { createSelector } from 'reselect'
import { matchPath } from 'react-router-dom'
import { logHeardError } from '@/utils/errorHelpers'
import { RouteConfig } from '@/components/routes/config'

export const selectUserPermissions = (state: ReduxState) =>
  state.auth.security.permissions

export const selectUserHasPermission = createSelector(
  selectUserPermissions,
  (_: unknown, permission: ProductFeatureId) => permission,
  (permissions, permission) => permissions.includes(permission)
)

export const selectUserHasPermissions = createSelector(
  selectUserPermissions,
  (_: unknown, permissions: ProductFeatureId[] | undefined) => permissions,
  (permissions, permissionsToCheck) =>
    permissionsToCheck
      ? permissionsToCheck.every((permission) =>
          permissions.includes(permission)
        )
      : true
)

const ABSOLUTE_URL = /^https?:\/\//

// The catch-all 404 route would match every path and hide genuine misses
const CATCH_ALL_ROUTE = '*'

export const hasPermissionsForRoute = (
  permissions: ProductFeatureId[],
  allRoutes: RouteConfig,
  routePath: string | undefined
): boolean => {
  if (!routePath || ABSOLUTE_URL.test(routePath)) {
    return true
  }
  const pathname = routePath.split('?')[0]
  if (!pathname) {
    return true
  }
  const routeEntries = Object.entries(allRoutes)
  const routeConfig =
    routeEntries.find(
      ([path]) => path === pathname || path === pathname.replace(/\/$/, '') // Remove trailing slash for comparison
    ) ??
    routeEntries.find(
      ([path]) => path !== CATCH_ALL_ROUTE && matchPath(path, pathname)
    )
  if (routeConfig) {
    const parentRoutesWithPermissionChecks = Object.entries(allRoutes).filter(
      ([path, route]) =>
        route.requiredPermissions?.includeChildRoutes &&
        pathname.startsWith(`${path}/`)
    )
    const isChildRoute = parentRoutesWithPermissionChecks.some(
      ([path, _route]) => pathname.startsWith(path)
    )
    let hasAllParentPermissions = true
    if (isChildRoute) {
      hasAllParentPermissions = parentRoutesWithPermissionChecks.every(
        ([_, parentRoute]) => {
          const requiredRoutePermissions =
            parentRoute.requiredPermissions?.features
          return requiredRoutePermissions
            ? requiredRoutePermissions.every((permission) =>
                permissions.includes(permission)
              )
            : true
        }
      )
    }
    const requiredRoutePermissions =
      routeConfig?.[1]?.requiredPermissions?.features
    const hasRequiredPermissions = requiredRoutePermissions
      ? requiredRoutePermissions.every((permission) =>
          permissions.includes(permission)
        )
      : true
    return hasAllParentPermissions && hasRequiredPermissions
  }
  logHeardError('Route config not found', { routePath })
  return true
}

export const selectUserHasPermissionsForRoute = createSelector(
  selectUserPermissions,
  (_: unknown, allRoutes: RouteConfig) => allRoutes,
  (_: unknown, __: unknown, routePath: string | undefined) => routePath,
  (permissions, allRoutes, routePath) =>
    hasPermissionsForRoute(permissions, allRoutes, routePath)
)
